import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, RefreshCw, TrendingUp, TrendingDown, Pencil, Check, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

type Category  = 'infra' | 'marketing' | 'operational' | 'other';
type Recurrence = 'monthly' | 'annual' | 'one-time';

interface ManualCost {
  id:         string;
  label:      string;
  category:   Category;
  amountBrl:  number;
  recurring:  Recurrence;
  note?:      string;
  startDate?: string; // YYYY-MM-DD
}

interface AutoCost {
  id:         string;
  label:      string;
  amountBrl:  number;
  note:       string;
  startDate?: string; // YYYY-MM-DD
}

const CATEGORY_LABELS: Record<Category, string> = {
  infra:       'Infraestrutura',
  marketing:   'Marketing',
  operational: 'Operacional',
  other:       'Outros',
};

const RECURRENCE_LABELS: Record<Recurrence, string> = {
  monthly:   'Mensal',
  annual:    'Anual',
  'one-time': 'Único',
};

const DEFAULT_AUTO_COSTS: AutoCost[] = [
  { id: 'auto_supabase',     label: 'Supabase (Pro plan)',  amountBrl: 100, note: 'Estimativa plano Pro' },
  { id: 'auto_resend',       label: 'Resend (email)',        amountBrl: 25,  note: 'Até 100k emails/mês' },
  { id: 'auto_vercel',       label: 'Vercel / Hosting',      amountBrl: 50,  note: 'Estimativa' },
  { id: 'auto_revenuecat',   label: 'RevenueCat (IAP)',       amountBrl: 0,   note: 'Grátis até U$2.5k MRR' },
  { id: 'auto_stripe',       label: 'Stripe',                amountBrl: 0,   note: '3.49% + R$0,39/transação (variável)' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function monthsSince(startDate: string): number {
  const start = new Date(startDate + 'T00:00:00');
  const now   = new Date();
  if (start > now) return 0;
  return Math.max(1, (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()));
}

function totalSpent(amountBrl: number, recurring: Recurrence, startDate?: string): string {
  if (!startDate || amountBrl === 0) return '-';
  const months = monthsSince(startDate);
  if (months === 0) return '-';
  if (recurring === 'one-time') return `R$ ${amountBrl.toFixed(2)}`;
  if (recurring === 'monthly')  return `R$ ${(amountBrl * months).toFixed(2)}`;
  // annual: full years elapsed
  const years = Math.max(1, Math.floor(months / 12));
  return `R$ ${(amountBrl * years).toFixed(2)}`;
}

function autoTotalSpent(amountBrl: number, startDate?: string): string {
  return totalSpent(amountBrl, 'monthly', startDate);
}

// ─── Shared inline-edit state ─────────────────────────────────────────────────

interface EditState {
  type:      'auto' | 'manual';
  id:        string;
  label:     string;
  amountBrl: string;
  note:      string;
  category:  Category;
  recurring: Recurrence;
  startDate: string;
}

export function AdminCostsPage() {
  const { showToast } = useToast();

  const [manualCosts, setManualCosts] = useState<ManualCost[]>([]);
  const [autoCosts,   setAutoCosts]   = useState<AutoCost[]>(DEFAULT_AUTO_COSTS);
  const [saving,      setSaving]      = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [aiReadings,  setAiReadings]  = useState(0);
  const [revenueBrl,  setRevenueBrl]  = useState(0);

  // ── Add-manual form ──────────────────────────────────────────────────────────
  const [addingCost,     setAddingCost]     = useState(false);
  const [newLabel,       setNewLabel]       = useState('');
  const [newCategory,    setNewCategory]    = useState<Category>('operational');
  const [newAmount,      setNewAmount]      = useState('');
  const [newRecur,       setNewRecur]       = useState<Recurrence>('monthly');
  const [newNote,        setNewNote]        = useState('');
  const [newStartDate,   setNewStartDate]   = useState('');

  // ── Add-auto form ────────────────────────────────────────────────────────────
  const [addingAuto,      setAddingAuto]     = useState(false);
  const [autoNewLabel,    setAutoNewLabel]   = useState('');
  const [autoNewAmt,      setAutoNewAmt]     = useState('');
  const [autoNewNote,     setAutoNewNote]    = useState('');
  const [autoNewStartDate,setAutoNewStartDate] = useState('');

  // ── Inline edit ──────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState<EditState | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86_400_000).toISOString();

    const [costsRes, autoRes, readingsRes, paymentsRes] = await Promise.all([
      supabase.from('platform_settings').select('value').eq('key', 'manual_costs').maybeSingle(),
      supabase.from('platform_settings').select('value').eq('key', 'auto_costs').maybeSingle(),
      supabase.from('reading_analytics').select('id', { count: 'exact', head: true }).gte('created_at', thirtyDaysAgo),
      supabase.from('payments').select('amount').eq('status', 'succeeded').gte('created_at', thirtyDaysAgo),
    ]);

    if (costsRes.data?.value) {
      try {
        const parsed = typeof costsRes.data.value === 'string'
          ? JSON.parse(costsRes.data.value) : costsRes.data.value;
        if (Array.isArray(parsed)) setManualCosts(parsed as ManualCost[]);
      } catch { /* use default */ }
    }

    if (autoRes.data?.value) {
      try {
        const parsed = typeof autoRes.data.value === 'string'
          ? JSON.parse(autoRes.data.value) : autoRes.data.value;
        if (Array.isArray(parsed) && parsed.length > 0) setAutoCosts(parsed as AutoCost[]);
      } catch { /* use defaults */ }
    }

    setAiReadings(readingsRes.count ?? 0);

    const total = (paymentsRes.data ?? []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    setRevenueBrl(total);
    setLoading(false);
  }

  async function saveCosts(silent = false) {
    setSaving(true);
    const [r1, r2] = await Promise.all([
      supabase.from('platform_settings').upsert({
        key: 'manual_costs', value: JSON.stringify(manualCosts),
        description: 'Custos manuais cadastrados pelo admin',
        updated_at: new Date().toISOString(),
      }, { onConflict: 'key' }),
      supabase.from('platform_settings').upsert({
        key: 'auto_costs', value: JSON.stringify(autoCosts),
        description: 'Custos automáticos editados pelo admin',
        updated_at: new Date().toISOString(),
      }, { onConflict: 'key' }),
    ]);
    setSaving(false);
    if (r1.error || r2.error) showToast({ type: 'error', message: 'Erro ao salvar custos' });
    else if (!silent) showToast({ type: 'success', message: 'Custos salvos!' });
  }

  // ── Add manual ───────────────────────────────────────────────────────────────
  function addManual() {
    if (!newLabel.trim() || !newAmount) return;
    setManualCosts(prev => [...prev, {
      id:        `cost_${Date.now()}`,
      label:     newLabel.trim(),
      category:  newCategory,
      amountBrl: parseFloat(newAmount) || 0,
      recurring: newRecur,
      note:      newNote.trim() || undefined,
      startDate: newStartDate || undefined,
    }]);
    setNewLabel(''); setNewAmount(''); setNewNote(''); setNewStartDate('');
    setAddingCost(false);
  }

  // ── Add auto ─────────────────────────────────────────────────────────────────
  function addAuto() {
    if (!autoNewLabel.trim()) return;
    setAutoCosts(prev => [...prev, {
      id:        `auto_${Date.now()}`,
      label:     autoNewLabel.trim(),
      amountBrl: parseFloat(autoNewAmt) || 0,
      note:      autoNewNote.trim(),
      startDate: autoNewStartDate || undefined,
    }]);
    setAutoNewLabel(''); setAutoNewAmt(''); setAutoNewNote(''); setAutoNewStartDate('');
    setAddingAuto(false);
  }

  // ── Remove ───────────────────────────────────────────────────────────────────
  function removeManual(id: string) { setManualCosts(prev => prev.filter(c => c.id !== id)); }
  function removeAuto(id: string)   { setAutoCosts(prev => prev.filter(c => c.id !== id)); }

  // ── Inline edit helpers ───────────────────────────────────────────────────────
  function startEditAuto(c: AutoCost) {
    setEditing({ type: 'auto', id: c.id, label: c.label, amountBrl: String(c.amountBrl), note: c.note, category: 'infra', recurring: 'monthly', startDate: c.startDate ?? '' });
  }
  function startEditManual(c: ManualCost) {
    setEditing({ type: 'manual', id: c.id, label: c.label, amountBrl: String(c.amountBrl), note: c.note ?? '', category: c.category, recurring: c.recurring, startDate: c.startDate ?? '' });
  }
  function cancelEdit() { setEditing(null); }

  async function confirmEdit() {
    if (!editing || !editing.label.trim()) return;
    const amount = parseFloat(editing.amountBrl) || 0;

    // Compute updated lists so we can save immediately with fresh values
    let newAutoCosts = autoCosts;
    let newManualCosts = manualCosts;

    if (editing.type === 'auto') {
      newAutoCosts = autoCosts.map(c => c.id === editing.id
        ? { ...c, label: editing.label.trim(), amountBrl: amount, note: editing.note.trim(), startDate: editing.startDate || undefined }
        : c);
      setAutoCosts(newAutoCosts);
    } else {
      newManualCosts = manualCosts.map(c => c.id === editing.id
        ? { ...c, label: editing.label.trim(), amountBrl: amount, note: editing.note.trim() || undefined, category: editing.category, recurring: editing.recurring, startDate: editing.startDate || undefined }
        : c);
      setManualCosts(newManualCosts);
    }
    setEditing(null);

    // Auto-save with computed values (bypasses async state)
    setSaving(true);
    const [r1, r2] = await Promise.all([
      supabase.from('platform_settings').upsert({
        key: 'manual_costs', value: JSON.stringify(newManualCosts),
        description: 'Custos manuais cadastrados pelo admin',
        updated_at: new Date().toISOString(),
      }, { onConflict: 'key' }),
      supabase.from('platform_settings').upsert({
        key: 'auto_costs', value: JSON.stringify(newAutoCosts),
        description: 'Custos automáticos editados pelo admin',
        updated_at: new Date().toISOString(),
      }, { onConflict: 'key' }),
    ]);
    setSaving(false);
    if (r1.error || r2.error) showToast({ type: 'error', message: 'Erro ao salvar custo' });
    else showToast({ type: 'success', message: 'Custo atualizado!' });
  }

  // ── Computed metrics ──────────────────────────────────────────────────────────
  const aiCostBrl     = Math.round(aiReadings * 0.12 * 100) / 100;
  const autoTotal     = autoCosts.reduce((s, c) => s + c.amountBrl, 0) + aiCostBrl;
  const manualMonthly = manualCosts.reduce((s, c) => {
    if (c.recurring === 'monthly') return s + c.amountBrl;
    if (c.recurring === 'annual')  return s + c.amountBrl / 12;
    return s;
  }, 0);
  const totalCosts = autoTotal + manualMonthly;
  const profit     = revenueBrl - totalCosts;

  const isEditingAuto   = (id: string) => editing?.type === 'auto'   && editing.id === id;
  const isEditingManual = (id: string) => editing?.type === 'manual' && editing.id === id;

  return (
    <div>
      <AdminPageHeader
        title="Custos & Lucro"
        objective="Acompanhe os custos operacionais da plataforma, automáticos e manuais, e visualize o lucro estimado com base na receita dos últimos 30 dias."
      />

      {/* Top metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <MetricCard label="Receita bruta (30d)"         value={`R$ ${revenueBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="#4ade80" />
        <MetricCard label="Custos totais/mês (est.)"    value={`R$ ${totalCosts.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="#fbbf24" />
        <MetricCard
          label="Lucro estimado"
          value={`R$ ${profit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          color={profit >= 0 ? '#4ade80' : '#f87171'}
          icon={profit >= 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
        />
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <>
          {/* ── Section 1: Custos automáticos ─────────────────────────────── */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <p style={{ fontSize: 'var(--text-caption)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                🔧 Custos Automáticos (estimativas das ferramentas integradas)
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setAddingAuto(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Plus size={13} /> Adicionar
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => saveCosts()} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Save size={13} /> {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>

            {addingAuto && (
              <div className="card" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>Nova ferramenta</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem' }}>
                  <input className="form-input" placeholder="Nome (ex: Sentry)" value={autoNewLabel} onChange={e => setAutoNewLabel(e.target.value)} />
                  <input className="form-input" placeholder="Valor/mês (R$)" type="number" min={0} step={0.01} value={autoNewAmt} onChange={e => setAutoNewAmt(e.target.value)} />
                  <input className="form-input" type="date" title="Data de início do custo" value={autoNewStartDate} onChange={e => setAutoNewStartDate(e.target.value)} />
                  <input className="form-input" placeholder="Observação (opcional)" value={autoNewNote} onChange={e => setAutoNewNote(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-primary btn-sm" onClick={addAuto} disabled={!autoNewLabel.trim()}>Adicionar</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAddingAuto(false)}>Cancelar</button>
                </div>
              </div>
            )}

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Ferramenta</th>
                    <th>Custo/mês (est.)</th>
                    <th>Data de início</th>
                    <th>Total gasto</th>
                    <th>Nota</th>
                    <th style={{ width: '72px' }} />
                  </tr>
                </thead>
                <tbody>
                  {/* Dynamic Claude API row always read-only */}
                  <tr>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Anthropic Claude API</td>
                    <td style={{ color: '#fbbf24', fontWeight: 700 }}>R$ {aiCostBrl.toFixed(2)}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>-</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>-</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>{aiReadings} leituras × R$0,12 (est. últimos 30d)</td>
                    <td />
                  </tr>

                  {autoCosts.map(c => isEditingAuto(c.id) && editing ? (
                    /* ── Edit row ── */
                    <tr key={c.id} style={{ background: 'rgba(31,78,95,0.07)' }}>
                      <td>
                        <input
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem' }}
                          value={editing.label}
                          onChange={e => setEditing(prev => prev && { ...prev, label: e.target.value })}
                          autoFocus
                        />
                      </td>
                      <td>
                        <input
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem', width: '100px' }}
                          type="number" min={0} step={0.01}
                          value={editing.amountBrl}
                          onChange={e => setEditing(prev => prev && { ...prev, amountBrl: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem', width: '130px' }}
                          type="date"
                          value={editing.startDate}
                          onChange={e => setEditing(prev => prev && { ...prev, startDate: e.target.value })}
                        />
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>—</td>
                      <td>
                        <input
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem' }}
                          value={editing.note}
                          onChange={e => setEditing(prev => prev && { ...prev, note: e.target.value })}
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button className="btn btn-ghost btn-sm" onClick={confirmEdit} title="Confirmar" style={{ color: '#4ade80', padding: '4px' }}>
                            <Check size={13} />
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={cancelEdit} title="Cancelar" style={{ padding: '4px' }}>
                            <X size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    /* ── Display row ── */
                    <tr key={c.id}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.label}</td>
                      <td style={{ color: c.amountBrl > 0 ? '#fbbf24' : 'var(--text-muted)', fontWeight: 700 }}>
                        {c.amountBrl > 0 ? `R$ ${c.amountBrl.toFixed(2)}` : '-'}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
                        {c.startDate ? new Date(c.startDate + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td style={{ color: c.startDate ? '#4ade80' : 'var(--text-muted)', fontSize: 'var(--text-caption)', fontWeight: c.startDate ? 600 : 400 }}>
                        {autoTotalSpent(c.amountBrl, c.startDate)}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>{c.note}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEditAuto(c)} title="Editar" style={{ padding: '4px' }}>
                            <Pencil size={13} />
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => removeAuto(c.id)} title="Remover" style={{ color: '#f87171', padding: '4px' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  <tr style={{ borderTop: '2px solid var(--border-subtle)', backgroundColor: 'rgba(31,78,95,0.04)' }}>
                    <td style={{ fontWeight: 700, color: 'var(--petrol)' }}>Total fixo estimado</td>
                    <td style={{ fontWeight: 700, color: 'var(--petrol)' }}>R$ {autoTotal.toFixed(2)}</td>
                    <td colSpan={4} />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Section 2: Custos manuais ──────────────────────────────────── */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <p style={{ fontSize: 'var(--text-caption)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                📋 Custos Manuais
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setAddingCost(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Plus size={13} /> Adicionar
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => saveCosts()} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Save size={13} /> {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>

            {addingCost && (
              <div className="card" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>Novo custo</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem' }}>
                  <input className="form-input" placeholder="Descrição (ex: Plano de internet)" value={newLabel} onChange={e => setNewLabel(e.target.value)} />
                  <input className="form-input" placeholder="Valor (R$)" type="number" min={0} step={0.01} value={newAmount} onChange={e => setNewAmount(e.target.value)} />
                  <select className="form-input" value={newCategory} onChange={e => setNewCategory(e.target.value as Category)}>
                    {(Object.keys(CATEGORY_LABELS) as Category[]).map(k => <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>)}
                  </select>
                  <select className="form-input" value={newRecur} onChange={e => setNewRecur(e.target.value as Recurrence)}>
                    {(Object.keys(RECURRENCE_LABELS) as Recurrence[]).map(k => <option key={k} value={k}>{RECURRENCE_LABELS[k]}</option>)}
                  </select>
                  <input className="form-input" type="date" title="Data de início do custo" value={newStartDate} onChange={e => setNewStartDate(e.target.value)} />
                </div>
                <input className="form-input" placeholder="Observação (opcional)" value={newNote} onChange={e => setNewNote(e.target.value)} />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-primary btn-sm" onClick={addManual} disabled={!newLabel.trim() || !newAmount}>Adicionar</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAddingCost(false)}>Cancelar</button>
                </div>
              </div>
            )}

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Descrição</th>
                    <th>Categoria</th>
                    <th>Valor</th>
                    <th>Recorrência</th>
                    <th>Data de início</th>
                    <th>Total gasto</th>
                    <th>Obs.</th>
                    <th style={{ width: '72px' }} />
                  </tr>
                </thead>
                <tbody>
                  {manualCosts.map(c => isEditingManual(c.id) && editing ? (
                    /* ── Edit row ── */
                    <tr key={c.id} style={{ background: 'rgba(31,78,95,0.07)' }}>
                      <td>
                        <input
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem' }}
                          value={editing.label}
                          onChange={e => setEditing(prev => prev && { ...prev, label: e.target.value })}
                          autoFocus
                        />
                      </td>
                      <td>
                        <select
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem' }}
                          value={editing.category}
                          onChange={e => setEditing(prev => prev && { ...prev, category: e.target.value as Category })}
                        >
                          {(Object.keys(CATEGORY_LABELS) as Category[]).map(k => <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>)}
                        </select>
                      </td>
                      <td>
                        <input
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem', width: '100px' }}
                          type="number" min={0} step={0.01}
                          value={editing.amountBrl}
                          onChange={e => setEditing(prev => prev && { ...prev, amountBrl: e.target.value })}
                        />
                      </td>
                      <td>
                        <select
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem' }}
                          value={editing.recurring}
                          onChange={e => setEditing(prev => prev && { ...prev, recurring: e.target.value as Recurrence })}
                        >
                          {(Object.keys(RECURRENCE_LABELS) as Recurrence[]).map(k => <option key={k} value={k}>{RECURRENCE_LABELS[k]}</option>)}
                        </select>
                      </td>
                      <td>
                        <input
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem', width: '130px' }}
                          type="date"
                          value={editing.startDate}
                          onChange={e => setEditing(prev => prev && { ...prev, startDate: e.target.value })}
                        />
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>—</td>
                      <td>
                        <input
                          className="form-input"
                          style={{ fontSize: 'var(--text-caption)', padding: '0.3rem 0.5rem' }}
                          value={editing.note}
                          onChange={e => setEditing(prev => prev && { ...prev, note: e.target.value })}
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button className="btn btn-ghost btn-sm" onClick={confirmEdit} title="Confirmar" style={{ color: '#4ade80', padding: '4px' }}>
                            <Check size={13} />
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={cancelEdit} title="Cancelar" style={{ padding: '4px' }}>
                            <X size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    /* ── Display row ── */
                    <tr key={c.id}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.label}</td>
                      <td>
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', background: 'rgba(31,78,95,0.1)', color: 'var(--petrol)' }}>
                          {CATEGORY_LABELS[c.category]}
                        </span>
                      </td>
                      <td style={{ fontWeight: 700, color: '#fbbf24' }}>R$ {c.amountBrl.toFixed(2)}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>{RECURRENCE_LABELS[c.recurring]}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
                        {c.startDate ? new Date(c.startDate + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td style={{ color: c.startDate ? '#4ade80' : 'var(--text-muted)', fontSize: 'var(--text-caption)', fontWeight: c.startDate ? 600 : 400 }}>
                        {totalSpent(c.amountBrl, c.recurring, c.startDate)}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>{c.note ?? '-'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEditManual(c)} title="Editar" style={{ padding: '4px' }}>
                            <Pencil size={13} />
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => removeManual(c.id)} title="Remover" style={{ color: '#f87171', padding: '4px' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {manualCosts.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                        Nenhum custo manual cadastrado ainda.
                      </td>
                    </tr>
                  )}
                  {manualCosts.length > 0 && (
                    <tr style={{ borderTop: '2px solid var(--border-subtle)', backgroundColor: 'rgba(31,78,95,0.04)' }}>
                      <td colSpan={2} style={{ fontWeight: 700, color: 'var(--petrol)' }}>Total manual/mês (est.)</td>
                      <td style={{ fontWeight: 700, color: 'var(--petrol)' }}>R$ {manualMonthly.toFixed(2)}</td>
                      <td colSpan={5} style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Anuais ÷ 12</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <RefreshCw size={13} /> Recarregar dados
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value, color, icon }: { label: string; value: string; color: string; icon?: React.ReactNode }) {
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      {icon && <div style={{ display: 'flex', justifyContent: 'center', color, marginBottom: '0.25rem' }}>{icon}</div>}
      <p style={{ fontSize: '1.5rem', fontWeight: 700, color, margin: '0 0 0.25rem' }}>{value}</p>
      <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{label}</p>
    </div>
  );
}
